import Link from 'next/link';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Loader, ActionIcon, Tooltip } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { IconX, IconChevronLeft, IconChevronRight, IconArrowDown } from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { aiChatLogsAtomFamily } from '~/atoms/ai-chat-atoms';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import { AIMessageRenderer } from './AIMessageRenderer';

export function AIChatPanel({ testContentId, onClose }: { testContentId: string; onClose?: () => void }) {
  const aiChatLogsAtom = aiChatLogsAtomFamily(testContentId);
  const [aiChatLogs, setAILogs] = useAtom(aiChatLogsAtom);
  const { vmStatus } = useLiveStateManager();

  const foregroundLog = aiChatLogs.logs[aiChatLogs.foregroundIndex] || [];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  const debouncedDisableAutoScroll = useDebouncedCallback((distanceToBottom: number) => {
    if (!autoScroll) {
      return;
    }
    if (distanceToBottom > 1) {
      setAutoScroll(false);
    }
  }, 100);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!autoScroll) {
        return;
      }
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      // Pass the calculated value to debounced function
      debouncedDisableAutoScroll(distanceToBottom);
    },
    [autoScroll, debouncedDisableAutoScroll],
  );

  // if auto-scroll is enabled, scroll to bottom when new logs come in
  useEffect(() => {
    if (scrollRef.current && autoScroll) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [foregroundLog, autoScroll]);

  // handle button click to scroll to bottom and re-enable auto-scroll
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
      setAutoScroll(true);
    }
  }, []);

  return (
    // Use flex column layout for the main container
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex flex-shrink-0 items-center justify-between border-b px-4 py-2 text-[11px] text-gray-600 dark:border-b-gray-600 dark:text-gray-400">
        <span className="flex items-center gap-2">
          <span className="font-mono text-xs font-medium">AI Logs</span>
          {aiChatLogs.logs.length > 1 && (
            <>
              <div className="ml-2 flex items-center gap-1">
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                  {aiChatLogs.foregroundIndex + 1} / {aiChatLogs.logs.length}
                </span>
              </div>
              <Tooltip label="Previous log">
                <ActionIcon
                  variant="subtle"
                  size="xs"
                  disabled={aiChatLogs.foregroundIndex === 0}
                  onClick={() => setAILogs({ type: 'navigatePrev' })}
                >
                  <IconChevronLeft size={12} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Next log">
                <ActionIcon
                  variant="subtle"
                  size="xs"
                  disabled={aiChatLogs.foregroundIndex === aiChatLogs.logs.length - 1}
                  onClick={() => setAILogs({ type: 'navigateNext' })}
                >
                  <IconChevronRight size={12} />
                </ActionIcon>
              </Tooltip>
            </>
          )}
        </span>
        {!!onClose && (
          <Button
            variant="subtle"
            size="compact-xs"
            onClick={onClose}
            className="text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <IconX size={14} />
          </Button>
        )}
      </div>

      {/* Flex container for reordering content, takes remaining vertical space */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* AICopilotInput Wrapper - its order changes based on logs.length */}
        {/* TODO: for now, we temporarily hide the copilot input box to focus on other features. In the future, we should re-enable it.
        When we re-enable it, we need to uncomment the div below and restore the original order classes:
        - Spacer: change from order-1 to order-2 (when logs.length === 0)
        - Link area: change from order-2 to order-3 (when logs.length === 0)
        */}

        {/* <div
          id={logs.length > 0 ? TOUR_ID.FLOATING_TOOLBAR_AI_ACTION : undefined}
          className={`flex-shrink-0 p-2 ${logs.length === 0 ? 'order-1' : 'order-3'}`}
        >
          <AICopilotInput testContentId={testContentId} />
        </div>
        */}

        {/* Logs display area OR Spacer - its order also changes */}
        {
          foregroundLog.length === 0 && vmStatus?.state !== 'replaying' ?
            // Spacer to push link to bottom when no logs
            // TODO: when we re-enable the copilot input box, we'll uncomment the div below and remove the div below it
            // <div className="order-2 flex-grow" />
            <div className="order-1 flex flex-grow items-center justify-center">
              <div className="px-4 text-center font-mono text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 hover:dark:text-gray-300">
                AI logs will show up here when it runs
              </div>
            </div>
            // Log display area (grows, shrinks, scrolls)
          : <div className="relative order-1 min-h-0 flex-grow">
              <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="scrollbar-thin scrollbar-thumb-gray-400/50 hover:scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600/50 dark:hover:scrollbar-thumb-gray-600 h-full overflow-y-auto overflow-x-hidden px-4 py-4 pb-8 focus:outline-none"
              >
                {foregroundLog.map((log, index) => (
                  <div
                    key={`${aiChatLogs.foregroundIndex}-${index}`}
                    className="mb-3 last:mb-0"
                  >
                    <AIMessageRenderer
                      message={log}
                      isLastMessage={index === foregroundLog.length - 1}
                      isPreviewing={vmStatus?.state === 'replaying'}
                    />
                  </div>
                ))}
                {aiChatLogs.foregroundIndex === aiChatLogs.logs.length - 1 && vmStatus?.state === 'replaying' && (
                  <div className="my-2 flex items-center justify-center gap-1 border-gray-300 py-4 text-xs text-stone-500 dark:border-gray-700 dark:text-stone-400">
                    <Loader
                      type="dots"
                      size="sm"
                    />
                    <span className="font-mono text-xs font-medium text-gray-500 dark:text-gray-400">
                      AI is working
                    </span>
                    <Loader
                      type="dots"
                      size="sm"
                    />
                  </div>
                )}
              </div>

              {/* Scroll to bottom button */}
              {!autoScroll && (
                <Tooltip label="Scroll to bottom">
                  <ActionIcon
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 shadow-lg transition-all duration-200 ease-in-out active:scale-95"
                    variant="filled"
                    size="md"
                    radius="xl"
                    onClick={scrollToBottom}
                  >
                    <IconArrowDown size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
            </div>

        }

        {/* Link area - only shown when no logs, appears at the bottom */}
        {/* TODO: when we re-enable the copilot input box, we need to change this to order-3 */}
        {foregroundLog.length === 0 && vmStatus?.state !== 'replaying' && (
          <div className="order-2 flex flex-shrink-0 flex-col items-start justify-center gap-2 px-4 py-2">
            <Link
              href="https://docs.stably.ai"
              target="_blank"
              className="flex w-full flex-row items-center justify-between gap-1 text-center font-mono text-[12px] text-gray-500 hover:text-gray-700 dark:text-gray-400 hover:dark:text-gray-300"
            >
              Stably docs
              <span>View</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
